import { IconPound } from '@iwoca/icons';
import { Input, Select } from '@iwoca/orion';
import { useFormContext } from 'react-hook-form';
import { TSignupFormData } from '../../SignupForm/SignupForm.types';
import { ConditionalField } from '../ConditionalField/ConditionalField';
import {
  FACILITY_FIELDS,
  FACILITY_OPTIONS,
  FACILITY_WITH_DESCRIPTION,
} from '../refinancing.types';

import styles from './RefinancingDebtFields.module.css';

type TProps = {
  index: number;
  disabled?: boolean;
};

export const RefinancingDebtFields = ({ index, disabled }: TProps) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<TSignupFormData>();

  const fieldName = `refinancing.debts.${index}` as const;
  // watch() returns '' before a facility is chosen (the Select's default value).
  const facilityType = watch(`${fieldName}.facilityType`);

  const conditionalFields = facilityType ? FACILITY_FIELDS[facilityType] : [];
  const debtErrors = errors.refinancing?.debts?.[index];

  return (
    <div className={styles.container}>
      <Input
        {...register(`${fieldName}.lenderName`)}
        label="Lender / Counterparty"
        type="text"
        error={debtErrors?.lenderName?.message}
        disabled={disabled}
      />
      <div className={styles.facility}>
        <Select
          {...register(`${fieldName}.facilityType`)}
          label="Type of facility"
          defaultValue=""
          error={debtErrors?.facilityType?.message}
          disabled={disabled}
        >
          <option value="" disabled>
            Choose an option
          </option>
          {FACILITY_OPTIONS.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {facilityType === FACILITY_WITH_DESCRIPTION && (
          <Input
            {...register(`${fieldName}.otherDescription`, {
              shouldUnregister: true,
            })}
            // Placeholder-only in the design (no visible label), so give it an
            // accessible name via aria-label.
            aria-label="Description of facility"
            placeholder="e.g. merchant cash advance, bridging loan"
            type="text"
            error={debtErrors?.otherDescription?.message}
            disabled={disabled}
          />
        )}
      </div>
      <Input
        {...register(`${fieldName}.outstandingBalance`)}
        label="Outstanding balance"
        iconLeft={<IconPound />}
        type="text"
        error={debtErrors?.outstandingBalance?.message}
        disabled={disabled}
      />
      {conditionalFields.map((field) => (
        <ConditionalField
          key={field.name}
          field={field}
          fieldName={fieldName}
          error={debtErrors?.[field.name]?.message}
          disabled={disabled}
        />
      ))}
    </div>
  );
};
